import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Loader2, ArrowLeft, Eye, Palette, Layout, Check } from 'lucide-react';
import { wedding } from '../../services/api';

// Import rendering functions from WeddingPage
// We'll re-use WeddingPage as a renderer by passing data as props

const API_URL = process.env.REACT_APP_API_URL || 'https://api.bridalconnect.co.za';

const TEMPLATES = [
  { id: 'classic', name: 'Classic', desc: 'Elegant serif fonts' },
  { id: 'modern', name: 'Modern', desc: 'Bold, clean sans-serif' },
  { id: 'rustic', name: 'Rustic', desc: 'Warm, natural feel' }
];

const COLOR_PALETTES = [
  { name: 'Protea Pink', primary: '#D4507A', secondary: '#F5E6CC' },
  { name: 'Bushveld Green', primary: '#5C7A3D', secondary: '#F0EDE5' },
  { name: 'Ocean Blue', primary: '#2E6B8A', secondary: '#E8F1F5' },
  { name: 'Sunset Gold', primary: '#D4913D', secondary: '#FFF8ED' },
  { name: 'Lavender Dream', primary: '#8B6BAE', secondary: '#F3EFF8' },
  { name: 'Classic Ivory', primary: '#8B7355', secondary: '#FFFFF0' },
  { name: 'Rose Garden', primary: '#C75B7A', secondary: '#FFF0F3' },
  { name: 'Midnight Elegance', primary: '#2C3E50', secondary: '#F8F9FA' }
];

// Template font classes (same as WeddingPage.js)
const TEMPLATE_FONTS = {
  classic: { heading: 'font-serif', body: 'font-serif', hero: 'font-serif italic', accent: 'tracking-wide' },
  modern: { heading: 'font-sans font-bold', body: 'font-sans', hero: 'font-sans font-black uppercase tracking-widest', accent: 'uppercase tracking-widest text-sm' },
  rustic: { heading: 'font-serif', body: 'font-sans', hero: 'font-serif', accent: 'tracking-wide' }
};

function getTemplateFonts(template) {
  return TEMPLATE_FONTS[template] || TEMPLATE_FONTS.classic;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-ZA', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });
}

function getImageUrl(url) {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_URL}${url.startsWith('/') ? '' : '/'}${url}`;
}

export default function PagePreview() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showControls, setShowControls] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Get overrides from query params, fallback to page data
  const templateParam = searchParams.get('template');
  const paletteParam = searchParams.get('palette');

  const [selectedTemplate, setSelectedTemplate] = useState(templateParam || 'classic');
  const [selectedPalette, setSelectedPalette] = useState(paletteParam || '');

  const fetchPage = useCallback(async () => {
    try {
      setLoading(true);
      const res = await wedding.getMyPage();
      setData(res.data);

      // Set initial selections from page data if no query params
      const pageData = res.data?.page;
      if (!templateParam && pageData?.template) {
        setSelectedTemplate(pageData.template);
      }
      if (!paletteParam && pageData?.color_scheme) {
        const match = COLOR_PALETTES.find(
          p => p.primary === pageData.color_scheme.primary
        );
        if (match) setSelectedPalette(match.name);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/couple/login');
        return;
      }
      setError('Failed to load page data');
    } finally {
      setLoading(false);
    }
  }, [navigate, templateParam, paletteParam]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  // Get active colours
  const getActiveColors = () => {
    const palette = COLOR_PALETTES.find(p => p.name === selectedPalette);
    if (palette) return { primary: palette.primary, secondary: palette.secondary, accent: palette.primary, background: palette.secondary };
    return data?.page?.color_scheme || { primary: '#D4507A', secondary: '#F5E6CC', accent: '#D4507A', background: '#F5E6CC' };
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const colors = getActiveColors();
      await wedding.updateMyPage({
        template: selectedTemplate,
        color_scheme: colors
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-rose-400 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Loading preview...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error || 'No data'}</p>
          <Link to="/couple/builder" className="text-rose-600 hover:text-rose-700 font-medium">Back to Builder</Link>
        </div>
      </div>
    );
  }

  const { page, sections = [] } = data;
  const template = selectedTemplate;
  const colors = getActiveColors();
  const fonts = getTemplateFonts(template);

  const sortedSections = [...sections]
    .filter(s => s.is_visible !== false)
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

  const pageStyle = {
    '--wedding-primary': colors.primary,
    '--wedding-secondary': colors.secondary,
    '--wedding-accent': colors.accent || colors.primary,
    '--wedding-bg': colors.background || '#ffffff'
  };

  const heroImage = getImageUrl(page.hero_image_url);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Floating control bar */}
      {showControls && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              {/* Back button */}
              <button
                onClick={() => navigate('/couple/builder')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800 text-sm font-medium flex-shrink-0"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back to Builder</span>
              </button>

              {/* Template selector */}
              <div className="flex items-center gap-2 overflow-x-auto">
                <Layout className="w-4 h-4 text-gray-400 flex-shrink-0" />
                {TEMPLATES.map(t => (
                  <button
                    key={t.id}
                    onClick={() => setSelectedTemplate(t.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                      selectedTemplate === t.id
                        ? 'bg-rose-100 text-rose-700 ring-2 ring-rose-300'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {t.name}
                  </button>
                ))}
              </div>

              {/* Colour palette selector */}
              <div className="flex items-center gap-1.5 overflow-x-auto flex-shrink-0">
                <Palette className="w-4 h-4 text-gray-400 flex-shrink-0" />
                {COLOR_PALETTES.map(p => (
                  <button
                    key={p.name}
                    onClick={() => setSelectedPalette(p.name)}
                    title={p.name}
                    className={`w-7 h-7 rounded-full border-2 flex-shrink-0 transition-all ${
                      selectedPalette === p.name
                        ? 'border-gray-800 scale-110'
                        : 'border-gray-200 hover:border-gray-400'
                    }`}
                    style={{ background: `linear-gradient(135deg, ${p.primary} 50%, ${p.secondary} 50%)` }}
                  />
                ))}
              </div>

              {/* Save button */}
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-rose-500 text-white rounded-lg text-sm font-medium hover:bg-rose-600 transition-colors disabled:opacity-50 flex-shrink-0"
              >
                {saved ? <Check className="w-4 h-4" /> : saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {saved ? 'Saved!' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toggle controls visibility */}
      <button
        onClick={() => setShowControls(!showControls)}
        className="fixed top-2 right-2 z-[60] p-2 bg-white rounded-full shadow-md text-gray-500 hover:text-gray-700"
        title={showControls ? 'Hide controls' : 'Show controls'}
      >
        <Eye className="w-4 h-4" />
      </button>

      {/* Wedding page preview */}
      <div className={showControls ? 'pt-16' : ''} style={pageStyle}>
        {/* Hero */}
        {sortedSections.find(s => s.section_type === 'hero') && (
          <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
            {heroImage && (
              <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${heroImage})` }} />
            )}
            <div className="absolute inset-0" style={{
              background: heroImage
                ? 'linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.5))'
                : 'var(--wedding-bg, #fdf2f8)'
            }} />
            <div className="relative z-10 text-center px-6 py-12 max-w-3xl mx-auto">
              <p className={`text-sm md:text-base mb-4 ${fonts.accent} ${heroImage ? 'text-white/80' : 'text-gray-500'}`}>
                We're getting married
              </p>
              <h1
                className={`text-4xl md:text-6xl lg:text-7xl mb-4 ${fonts.hero} ${heroImage ? 'text-white' : ''}`}
                style={!heroImage ? { color: 'var(--wedding-primary)' } : {}}
              >
                {page.partner1_name} <span className={`${heroImage ? 'text-white/70' : 'text-gray-400'} mx-2`}>&</span> {page.partner2_name}
              </h1>
              {page.wedding_date && (
                <p className={`text-lg md:text-xl mb-2 ${fonts.body} ${heroImage ? 'text-white/90' : 'text-gray-600'}`}>
                  {formatDate(page.wedding_date)}
                </p>
              )}
              {page.venue_name && (
                <p className={`text-base md:text-lg mb-8 ${fonts.body} ${heroImage ? 'text-white/80' : 'text-gray-500'}`}>
                  {page.venue_name}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Other sections - simplified preview */}
        {sortedSections.filter(s => s.section_type !== 'hero').map((section) => {
          const content = section.content || {};
          const isAlt = ['schedule', 'travel', 'rsvp', 'faq'].includes(section.section_type);

          return (
            <div
              key={section.id}
              className="py-16 md:py-24 px-6"
              style={isAlt ? { backgroundColor: 'var(--wedding-secondary)' } : {}}
            >
              <div className="max-w-3xl mx-auto text-center">
                <h2
                  className={`text-2xl md:text-3xl mb-6 ${fonts.heading}`}
                  style={{ color: 'var(--wedding-primary)' }}
                >
                  {section.title}
                </h2>

                {section.section_type === 'our_story' && content.text && (
                  <p className={`text-gray-600 leading-relaxed whitespace-pre-line ${fonts.body}`}>
                    {content.text.substring(0, 300)}{content.text.length > 300 ? '...' : ''}
                  </p>
                )}

                {section.section_type === 'schedule' && (
                  <p className={`text-gray-500 ${fonts.body}`}>Your event schedule will appear here</p>
                )}

                {section.section_type === 'wedding_party' && (
                  <p className={`text-gray-500 ${fonts.body}`}>Your wedding party will appear here</p>
                )}

                {section.section_type === 'gallery' && (
                  <div className="grid grid-cols-3 gap-2 max-w-md mx-auto">
                    {[1,2,3].map(i => (
                      <div key={i} className="aspect-square bg-gray-200 rounded-lg" />
                    ))}
                  </div>
                )}

                {section.section_type === 'rsvp' && (
                  <div className="max-w-sm mx-auto">
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                      <p className={`text-gray-600 mb-4 ${fonts.body}`}>Find your name to RSVP</p>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          disabled
                          placeholder="Enter your full name"
                          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-50"
                        />
                        <button
                          disabled
                          className="px-4 py-2 rounded-lg text-white text-sm font-medium"
                          style={{ backgroundColor: 'var(--wedding-primary)' }}
                        >
                          Find
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {section.section_type === 'faq' && content.items?.length > 0 && (
                  <div className="text-left max-w-lg mx-auto space-y-2">
                    {content.items.slice(0, 3).map((item, i) => (
                      <div key={i} className="bg-white rounded-lg p-4 border border-gray-100">
                        <p className={`font-medium text-gray-800 ${fonts.heading}`}>{item.question}</p>
                      </div>
                    ))}
                  </div>
                )}

                {section.section_type === 'vendors' && (
                  <p className={`text-gray-500 ${fonts.body}`}>Your wedding vendors will appear here</p>
                )}

                {section.section_type === 'travel' && (
                  <p className={`text-gray-500 ${fonts.body}`}>Travel & accommodation info will appear here</p>
                )}
              </div>
            </div>
          );
        })}

        {/* Footer */}
        <div className="py-8 px-6 text-center border-t border-gray-200">
          <p className={`text-gray-400 text-sm ${fonts.body}`}>
            {page.partner1_name} & {page.partner2_name}
            {page.wedding_date && ` | ${formatDate(page.wedding_date)}`}
          </p>
        </div>
      </div>
    </div>
  );
}
